import { Controller } from "@hotwired/stimulus"

// Instant product image swapping without server round-trips
export default class extends Controller {
  static targets = ["mainImage", "thumbnail", "modal", "modalMainImage", "modalThumbnail", "modalViewport"]

  connect() {
    this.fadeDuration = 180
    this.slideDuration = 300
    this.isTransitioning = false
    this.pendingIndex = null
    this.modalHideTimeout = null
    this.modalZoomScale = 2
    this.modalPanSensitivity = 250
    this.modalPanRafId = null
    this.pendingPanRatios = null
    this.currentIndex = this.currentImageIndex()
    this.preloadImages()
    this.showImageByIndex(this.currentIndex, { skipTransition: true })

    this.boundHandleKeydown = this.handleKeydown.bind(this)
    this.boundHandleModalMouseMove = this.handleModalMouseMove.bind(this)
    this.boundHandleModalMouseLeave = this.handleModalMouseLeave.bind(this)
    this.boundFlushModalPan = this.flushModalPan.bind(this)
    window.addEventListener("keydown", this.boundHandleKeydown)
    this.bindModalZoomEvents()
  }

  disconnect() {
    window.removeEventListener("keydown", this.boundHandleKeydown)
    this.unbindModalZoomEvents()
    this.cancelPendingPanFrame()
    if (this.modalHideTimeout) {
      window.clearTimeout(this.modalHideTimeout)
      this.modalHideTimeout = null
    }
    this.unlockScroll()
  }

  swapImage(event) {
    event.preventDefault()

    const targetIndex = parseInt(event.currentTarget.dataset.index, 10)
    if (!Number.isNaN(targetIndex)) {
      this.showImageByIndex(targetIndex)
    }
  }

  selectModalImage(event) {
    event.preventDefault()

    const targetIndex = parseInt(event.currentTarget.dataset.index, 10)
    if (!Number.isNaN(targetIndex)) {
      this.showImageByIndex(targetIndex)
    }
  }

  previousImage(event) {
    event.preventDefault()
    if (this.thumbnailTargets.length === 0) return

    this.showImageByIndex(this.currentIndex - 1)
  }

  nextImage(event) {
    event.preventDefault()
    if (this.thumbnailTargets.length === 0) return

    this.showImageByIndex(this.currentIndex + 1)
  }

  previousModalImage(event) {
    this.previousImage(event)
  }

  nextModalImage(event) {
    this.nextImage(event)
  }

  openModal(event) {
    event.preventDefault()
    if (!this.hasModalTarget) return

    if (this.modalHideTimeout) {
      window.clearTimeout(this.modalHideTimeout)
      this.modalHideTimeout = null
    }

    this.showImageByIndex(this.currentIndex, { skipTransition: true })
    this.modalTarget.classList.remove("hidden")
    window.requestAnimationFrame(() => {
      this.modalTarget.classList.remove("opacity-0", "pointer-events-none")
      this.resetModalZoom()
    })
    this.lockScroll()
  }

  closeModal(event) {
    if (event) event.preventDefault()
    if (!this.hasModalTarget) return

    this.resetModalZoom()
    this.modalTarget.classList.add("opacity-0", "pointer-events-none")
    this.modalHideTimeout = window.setTimeout(() => {
      this.modalTarget.classList.add("hidden")
      this.modalHideTimeout = null
    }, this.fadeDuration)
    this.unlockScroll()
  }

  backdropClose(event) {
    if (event.target === this.modalTarget) {
      this.closeModal()
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  handleKeydown(event) {
    if (!this.isModalOpen()) return

    if (event.key === "Escape") {
      this.closeModal()
      return
    }

    if (event.key === "ArrowLeft") {
      event.preventDefault()
      this.showImageByIndex(this.currentIndex - 1)
      return
    }

    if (event.key === "ArrowRight") {
      event.preventDefault()
      this.showImageByIndex(this.currentIndex + 1)
    }
  }

  preloadImages() {
    this.thumbnailTargets.forEach((thumbnail) => {
      const imageUrl = thumbnail.dataset.imageUrl
      if (!imageUrl) return

      const image = new Image()
      image.src = imageUrl
    })
  }

  currentMainImageUrl() {
    return this.normalizeUrl(this.mainImageTarget?.currentSrc || this.mainImageTarget?.src || "")
  }

  currentImageIndex() {
    if (!this.hasMainImageTarget) return 0

    const currentUrl = this.currentMainImageUrl()
    const index = this.thumbnailTargets.findIndex(
      (thumbnail) => this.normalizeUrl(thumbnail.dataset.imageUrl) === currentUrl
    )

    return index >= 0 ? index : 0
  }

  showImageByIndex(index, { skipTransition = false } = {}) {
    if (this.thumbnailTargets.length === 0) return

    const boundedIndex =
      ((index % this.thumbnailTargets.length) + this.thumbnailTargets.length) %
      this.thumbnailTargets.length
    const selectedThumbnail = this.thumbnailTargets[boundedIndex]
    const selectedUrl = selectedThumbnail?.dataset.imageUrl

    if (!selectedUrl) return

    if (
      !skipTransition &&
      this.normalizeUrl(selectedUrl) === this.currentMainImageUrl()
    ) {
      this.currentIndex = boundedIndex
      this.updateActiveThumbnails(boundedIndex)
      return
    }

    if (this.isTransitioning && !skipTransition) {
      this.pendingIndex = boundedIndex
      this.updateActiveThumbnails(boundedIndex)
      return
    }

    const previousIndex = this.currentIndex
    const direction = this.transitionDirection(index, boundedIndex, previousIndex)

    this.currentIndex = boundedIndex
    this.updateActiveThumbnails(boundedIndex)

    if (skipTransition) {
      this.setImageSources(selectedUrl)
      return
    }

    this.transitionToImage(selectedUrl, direction)
  }

  transitionDirection(requestedIndex, boundedIndex, previousIndex = this.currentIndex) {
    if (this.thumbnailTargets.length < 2) return 1
    if (requestedIndex > previousIndex) return 1
    if (requestedIndex < previousIndex) return -1
    return boundedIndex > previousIndex ? 1 : -1
  }

  normalizeUrl(url) {
    if (!url) return ""

    try {
      return new URL(url, window.location.href).href
    } catch {
      return url
    }
  }

  transitionToImage(selectedUrl, direction = 1) {
    if (!this.hasMainImageTarget) {
      this.setImageSources(selectedUrl)
      return
    }

    this.isTransitioning = true
    this.pendingIndex = null

    if (this.hasModalMainImageTarget) {
      this.modalMainImageTarget.src = selectedUrl
      this.resetModalZoom()
    }

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.setImageSources(selectedUrl)
      this.completeTransition()
      return
    }

    const outgoingImage = this.mainImageTarget
    const frame = outgoingImage.parentElement
    const incomingImage = outgoingImage.cloneNode()
    const incomingStart = `${direction * 100}%`
    const outgoingEnd = `${direction * -100}%`
    const transition = `transform ${this.slideDuration}ms cubic-bezier(0.22, 1, 0.36, 1)`
    const computedStyle = window.getComputedStyle(outgoingImage)
    const imageHeight = outgoingImage.getBoundingClientRect().height

    incomingImage.src = selectedUrl
    incomingImage.classList.remove("transition-opacity", "duration-200", "ease-out", "opacity-0")
    incomingImage.removeAttribute("data-product-gallery-target")
    this.prepareSlidingImage(outgoingImage, computedStyle, "translateX(0)", transition)
    this.prepareSlidingImage(incomingImage, computedStyle, `translateX(${incomingStart})`, transition)

    if (frame && imageHeight > 0) {
      frame.style.height = `${imageHeight}px`
    }

    frame.appendChild(incomingImage)

    window.requestAnimationFrame(() => {
      outgoingImage.style.transform = `translateX(${outgoingEnd})`
      incomingImage.style.transform = "translateX(0)"
    })

    window.setTimeout(() => {
      outgoingImage.src = selectedUrl
      this.resetSlidingImage(outgoingImage)
      if (frame) frame.style.height = ""
      incomingImage.remove()
      this.completeTransition()
    }, this.slideDuration)
  }

  prepareSlidingImage(image, computedStyle, transformValue, transitionValue) {
    image.style.position = "absolute"
    image.style.inset = "0"
    image.style.width = "100%"
    image.style.height = "100%"
    image.style.maxWidth = "100%"
    image.style.maxHeight = "100%"
    image.style.objectFit = computedStyle.objectFit
    image.style.objectPosition = computedStyle.objectPosition
    image.style.transform = transformValue
    image.style.transition = transitionValue
  }

  resetSlidingImage(image) {
    image.style.position = ""
    image.style.inset = ""
    image.style.width = ""
    image.style.height = ""
    image.style.maxWidth = ""
    image.style.maxHeight = ""
    image.style.objectFit = ""
    image.style.objectPosition = ""
    image.style.transform = ""
    image.style.transition = ""
  }

  completeTransition() {
    this.isTransitioning = false

    if (
      this.pendingIndex !== null &&
      this.pendingIndex !== this.currentIndex
    ) {
      const nextIndex = this.pendingIndex
      this.pendingIndex = null
      this.showImageByIndex(nextIndex)
    }
  }

  setImageSources(selectedUrl) {
    if (this.hasMainImageTarget) {
      this.mainImageTarget.src = selectedUrl
    }

    if (this.hasModalMainImageTarget) {
      this.modalMainImageTarget.src = selectedUrl
      this.resetModalZoom()
    }
  }

  updateActiveThumbnails(activeIndex) {
    this.thumbnailTargets.forEach((thumbnail, index) => {
      const isActive = index === activeIndex
      thumbnail.classList.toggle("border-slate-700/50", isActive)
      thumbnail.classList.toggle("border-transparent", !isActive)
      thumbnail.classList.toggle("hover:border-slate-700", !isActive)
    })

    if (this.hasModalThumbnailTarget) {
      this.modalThumbnailTargets.forEach((thumbnail, index) => {
        const isActive = index === activeIndex
        thumbnail.classList.toggle("border-white", isActive)
        thumbnail.classList.toggle("hover:border-white/70", !isActive)
      })
    }
  }

  isModalOpen() {
    return this.hasModalTarget && !this.modalTarget.classList.contains("hidden")
  }

  lockScroll() {
    document.documentElement.classList.add("modal-locked")
    document.body.classList.add("modal-locked")
  }

  unlockScroll() {
    document.documentElement.classList.remove("modal-locked")
    document.body.classList.remove("modal-locked")
  }

  bindModalZoomEvents() {
    if (!this.hasModalViewportTarget) return

    this.modalViewportTarget.addEventListener("mousemove", this.boundHandleModalMouseMove)
    this.modalViewportTarget.addEventListener("mouseleave", this.boundHandleModalMouseLeave)
  }

  unbindModalZoomEvents() {
    if (!this.hasModalViewportTarget) return

    this.modalViewportTarget.removeEventListener("mousemove", this.boundHandleModalMouseMove)
    this.modalViewportTarget.removeEventListener("mouseleave", this.boundHandleModalMouseLeave)
  }

  handleModalMouseMove(event) {
    if (!this.isModalOpen()) return
    if (!this.hasModalViewportTarget || !this.hasModalMainImageTarget) return

    const viewportRect = this.modalViewportTarget.getBoundingClientRect()
    if (viewportRect.width <= 0 || viewportRect.height <= 0) return

    const clampedX = Math.min(Math.max(event.clientX - viewportRect.left, 0), viewportRect.width)
    const clampedY = Math.min(Math.max(event.clientY - viewportRect.top, 0), viewportRect.height)
    this.pendingPanRatios = {
      x: clampedX / viewportRect.width,
      y: clampedY / viewportRect.height
    }

    // Disable transition while actively panning to avoid directional lag.
    this.modalMainImageTarget.style.transition = "none"

    if (!this.modalPanRafId) {
      this.modalPanRafId = window.requestAnimationFrame(this.boundFlushModalPan)
    }
  }

  handleModalMouseLeave() {
    this.cancelPendingPanFrame()
    this.resetModalZoom()
  }

  resetModalZoom() {
    if (!this.hasModalMainImageTarget) return

    this.pendingPanRatios = null
    this.modalMainImageTarget.style.transformOrigin = "center center"
    this.modalMainImageTarget.style.transition = "transform 120ms ease-out"
    this.modalMainImageTarget.style.transform = "scale(1) translate(0, 0)"
  }

  flushModalPan() {
    this.modalPanRafId = null
    if (!this.pendingPanRatios || !this.hasModalMainImageTarget) return

    const moveX = (this.pendingPanRatios.x - 0.5) * -this.modalPanSensitivity
    const moveY = (this.pendingPanRatios.y - 0.5) * -this.modalPanSensitivity

    this.modalMainImageTarget.style.transformOrigin = "center center"
    this.modalMainImageTarget.style.transform = `scale(${this.modalZoomScale}) translate(${moveX}px, ${moveY}px)`
  }

  cancelPendingPanFrame() {
    if (!this.modalPanRafId) return

    window.cancelAnimationFrame(this.modalPanRafId)
    this.modalPanRafId = null
  }
}
